import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Users, Crown, Shield, ChevronRight, LogOut, Check, Heart, Bell, Phone, Mail, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { Editable } from "../components/Editable";
import { BrandMultiSelect } from "../components/BrandMultiSelect";
import { useAuth } from "../context/AuthContext";
import { HIDE_LOVED, PRELAUNCH_FREE } from "../lib/siteConfig";

export default function MyUptoo() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [family, setFamily] = useState(null);

  useEffect(() => { api.get("/family").then(({ data }) => setFamily(data)).catch(() => {}); }, []);

  const premium = !!user.is_premium;
  const ps = user.plan_status;
  const planLabel = PRELAUNCH_FREE && ps !== "premium" && ps !== "comp" ? "Free during prelaunch"
    : ps === "premium" ? "Premium"
    : ps === "comp" ? "Complimentary"
    : ps === "trial" ? `Free trial · ${user.trial_days_remaining}d left`
    : "Free";

  return (
    <div className="max-w-2xl mx-auto space-y-6 fade-up">
      <div className="bg-white rounded-3xl border border-line shadow-card p-6 flex flex-wrap items-center gap-4">
        <div className="w-16 h-16 rounded-3xl bg-brand-light flex items-center justify-center"><User className="text-brand" size={30} /></div>
        <div className="flex-1 min-w-[12rem]">
          <h1 className="font-heading font-black text-2xl text-ink break-words" data-testid="account-heading">{user.name || "My account"}</h1>
          <p className="text-ink-soft text-sm break-all">{user.email}</p>
        </div>
        <span data-testid="membership-badge" className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold w-max sm:w-auto sm:ml-auto ${premium && !PRELAUNCH_FREE ? "bg-gold/15 text-gold-text border border-gold/30" : "bg-cream text-ink-muted border border-line"}`}>
          {planLabel}
        </span>
      </div>

      <FollowingSection user={user} />
      {PRELAUNCH_FREE && (
        <div className="bg-blush/60 rounded-3xl border border-line p-5" data-testid="me-prelaunch-note">
          <p className="font-heading font-extrabold text-base text-ink">Your family, sizes and preferences.</p>
          <p className="text-sm text-ink-soft mt-1">Sizing assistance for the launch brands and saved searches are free right now. Matches for saved searches appear in Ding Me. Email digests and text alerts: coming soon.</p>
        </div>
      )}
      {!PRELAUNCH_FREE && !premium && (
        <button onClick={() => navigate("/upgrade")} data-testid="me-upgrade-btn" className="w-full bg-brand rounded-3xl shadow-card p-6 text-left text-white hover:shadow-lift hover:-translate-y-0.5 transition-all flex items-center justify-between">
          <div>
            <p className="font-heading font-extrabold text-xl flex items-center gap-2"><Crown size={22} /> <Editable id="me.gopremium-title">Go Premium</Editable></p>
            <Editable as="p" id="me.gopremium-sub" className="text-white/85 text-sm mt-1">Unlock all purchase links, full matching & unlimited alerts.</Editable>
          </div>
          <ChevronRight size={24} />
        </button>
      )}

      <Row icon={Users} title="Family Profile" eid="me.row.family" subtitle={family && family.children ? `${family.children.length} ${family.children.length === 1 ? "child" : "children"} added` : "Not set up yet"} onClick={() => navigate("/family-profile")} testid="me-family-row" />
      {!HIDE_LOVED && (
        <Row icon={Heart} title="Saved collections" eid="me.row.loved" subtitle="Collections you hearted to revisit" onClick={() => navigate("/loved")} testid="me-loved-row" />
      )}
      {!PRELAUNCH_FREE && <Row icon={Crown} title="Membership & Billing" eid="me.row.billing" subtitle={planLabel} onClick={() => navigate("/upgrade")} testid="me-billing-row" />}
      {user.role === "admin" && (
        <Row icon={Shield} title="Admin Panel" eid="me.row.admin" subtitle="Manage listings, collections & AI" onClick={() => navigate("/admin")} testid="me-admin-row" />
      )}

      <NotificationPrefs user={user} refresh={refresh} />

      <FavoriteBrands user={user} refresh={refresh} />

      {premium && !PRELAUNCH_FREE && (
        <div className="bg-white rounded-3xl border border-line p-6">
          <Editable as="h3" id="me.perks-title" className="font-heading font-bold text-lg text-ink mb-3">Your premium perks</Editable>
          {["All retailer purchase links unlocked", "Full family matching", "Unlimited & early sale alerts"].map((p) => (
            <p key={p} className="flex items-center gap-2 text-ink-soft py-1"><Check size={16} className="text-success" /> {p}</p>
          ))}
        </div>
      )}

      <button onClick={() => { logout(); navigate("/"); }} data-testid="me-logout-btn" className="w-full h-14 rounded-full border-2 border-line text-ink font-bold flex items-center justify-center gap-2 hover:border-brand/30 transition-all">
        <LogOut size={18} /> <Editable id="me.logout">Log out</Editable>
      </button>
    </div>
  );
}

function FollowingSection({ user }) {
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [feed, setFeed] = useState(null);
  const load = () => { api.get("/me/following").then(({ data }) => setF(data.following)).catch(() => setF([])); api.get("/me/feed").then(({ data }) => setFeed(data)).catch(() => setFeed({ items: [], preferences: {} })); };
  useEffect(() => { load(); }, []);
  const setMode = async (handle, mode) => { try { const { data } = await api.put(`/contributors/${handle}/follow-preferences`, { mode }); toast.success(data.note); load(); } catch { toast.error("Could not save"); } };
  const setDing = async (handle, ding_alerts) => { try { const { data } = await api.put(`/contributors/${handle}/follow-preferences`, { ding_alerts }); toast.success(data.note); load(); } catch { toast.error("Could not save"); } };
  const feedLine = (it) => <span className="min-w-0 truncate"><b>{it.look.contributor.display_name}</b> published “{it.look.name}” on {it.collection_name} (rev {it.revision}){it.is_demo ? " · demo" : ""}</span>;
  return (
    <div className="bg-white rounded-3xl border border-line shadow-soft p-6 space-y-3" data-testid="me-following">
      <div><h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Users size={18} className="text-brand" /> Contributors you follow</h3><p className="text-sm text-ink-muted mt-1">Saved sizes and family details are visible only to you. Contributors cannot see them or your contact information. Following never subscribes you to email or texts.</p></div>
      {f === null ? null : f.length === 0 ? <p className="text-sm text-ink-muted" data-testid="me-following-empty">You aren't following anyone yet. Find contributors on any collection's community looks.</p> : (
        <ul className="space-y-2">{f.map((c) => <li key={c.handle} className="flex items-center justify-between gap-3 rounded-2xl border border-line p-3" data-testid={`me-following-${c.handle}`}><div className="flex items-center gap-3 min-w-0"><span className="h-10 w-10 rounded-full bg-brand text-white font-heading font-black text-sm flex items-center justify-center shrink-0">{c.initials}</span><div className="min-w-0"><p className="font-bold text-ink text-sm truncate">{c.display_name}</p><p className="text-xs text-ink-muted">@{c.handle} · {c.approved_looks} approved look{c.approved_looks === 1 ? "" : "s"}</p><select value={c.mode || "all"} onChange={(e) => setMode(c.handle, e.target.value)} data-testid={`follow-mode-${c.handle}`} aria-label="Following feed preference" className="mt-1 h-8 rounded-full border border-line bg-white px-2 text-xs"><option value="all">All new looks</option><option value="matching">Only matching my saved searches</option></select><label className="mt-1 flex items-center gap-1.5 text-xs text-ink-soft"><input type="checkbox" checked={!!c.ding_alerts} onChange={(e) => setDing(c.handle, e.target.checked)} data-testid={`follow-ding-${c.handle}`} className="accent-brand" /> Also ding me in-app (Ding Me) for new looks</label></div></div><button onClick={() => navigate(`/u/${c.handle}`)} data-testid={`me-explore-${c.handle}`} className="h-9 px-3 rounded-full border border-line bg-white text-xs font-bold text-ink-soft hover:text-brand shrink-0">Explore</button></li>)}</ul>
      )}
      {feed && (feed.items.length > 0 || (feed.withheld || []).length > 0) && <div data-testid="me-feed"><p className="text-xs font-bold text-ink mt-2">Following feed <span className="text-ink-muted font-semibold">· in-app only</span></p>
        {feed.items.length > 0 && <ul className="mt-1 space-y-1">{feed.items.slice(0, 8).map((it) => <li key={it.id} className="text-sm flex items-center justify-between gap-2 rounded-2xl bg-cream/60 border border-line p-2" data-testid={`feed-item-${it.id}`}><span className="min-w-0 truncate">{feedLine(it)}{it.match?.status === "matched" && <span className="ml-1 text-[11px] font-bold text-success" data-testid={`feed-match-${it.id}`}>· matches “{it.match.search_name}”</span>}</span><button onClick={() => navigate(`/c/${it.look.collection_id}?look=${it.look.id}`)} className="h-8 px-3 rounded-full border border-line bg-white text-xs font-bold shrink-0">Open</button></li>)}</ul>}
        {(feed.withheld || []).length > 0 && <div className="mt-2" data-testid="me-feed-withheld"><p className="text-[11px] font-bold text-ink-muted">Not shown as matches ({feed.withheld.length}) — “Only matching” needs evidence for every rule of your saved searches</p><ul className="mt-1 space-y-1">{feed.withheld.slice(0, 6).map((it) => <li key={it.id} className="text-xs flex items-center justify-between gap-2 rounded-2xl border border-dashed border-line p-2 text-ink-soft" data-testid={`feed-withheld-${it.id}`}><span className="min-w-0"><span className="block truncate">{feedLine(it)}</span><span className="block text-ink-muted" data-testid={`feed-withheld-reason-${it.id}`}>{it.status === "insufficient_evidence" ? "Insufficient evidence" : "Not a match"} · {it.detail}</span></span><button onClick={() => navigate(`/c/${it.look.collection_id}?look=${it.look.id}`)} className="h-8 px-3 rounded-full border border-line bg-white text-xs font-bold shrink-0">Open anyway</button></li>)}</ul></div>}
      </div>}
      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={() => navigate("/studio")} data-testid="me-studio-link" className="h-10 px-4 rounded-full bg-brand text-white text-xs font-bold">My contributor studio</button>
        {(user.affiliate || user.role === "admin") && <button onClick={() => navigate("/partner-desk")} data-testid="me-partner-desk-link" className="h-10 px-4 rounded-full border border-line bg-white text-xs font-bold text-ink-soft">Partner desk</button>}
      </div>
    </div>
  );
}

const CHANNELS = [
  { key: "in_app", label: "In-app", icon: Bell },
  { key: "email", label: "Email digest", icon: Mail },
  { key: "sms", label: "SMS text", icon: Phone },
];

function NotificationPrefs({ user, refresh }) {
  const initialPhone = user.phone || "";
  const initialEmail = user.notify_email || user.email || "";
  const initialChannels = user.notify_channels && user.notify_channels.length ? user.notify_channels : ["in_app"];
  const [phone, setPhone] = useState(initialPhone);
  const [notifyEmail, setNotifyEmail] = useState(initialEmail);
  const [channels, setChannels] = useState(initialChannels);
  const [saving, setSaving] = useState(false);
  const [caps, setCaps] = useState(null);
  useEffect(() => { api.get("/email/capabilities").then(({ data }) => setCaps(data)).catch(() => setCaps({})); }, []);
  // Per-flow runtime state: digests count as live only when the owner has verified real delivery. SMS is never live here.
  const emailLive = !!caps?.saved_search_digest?.verified_live;
  const availability = { in_app: "Available now", email: emailLive ? "Available now" : "Coming soon", sms: "Coming soon" };

  const toggle = (k) => setChannels((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));

  const wantEmail = channels.includes("email");
  const wantSms = channels.includes("sms");
  const emailValid = !wantEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail.trim());
  const smsValid = !wantSms || phone.trim().replace(/\D/g, "").length >= 10;
  const sameChannels = channels.slice().sort().join(",") === initialChannels.slice().sort().join(",");
  const changed = !(sameChannels && phone === initialPhone && notifyEmail === initialEmail);
  const canSave = changed && emailValid && smsValid && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.put("/profile/notifications", { phone, notify_email: notifyEmail, notify_channels: channels.length ? channels : ["in_app"] });
      await refresh();
      toast.success("Preference saved — nothing is sent until that channel launches and you turn it on per saved search.");
    } catch {
      toast.error("Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-line shadow-soft p-6 space-y-4" data-testid="notify-prefs">
      <div>
        <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Bell size={18} className="text-brand" /> How should we notify you?</h3>
        <p className="text-sm text-ink-muted mt-1">Choosing a channel saves a preference only — it does not enrol you in anything.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-2">
        {CHANNELS.map(({ key, label, icon: Icon }) => {
          const active = channels.includes(key);
          const live = availability[key] === "Available now";
          return (
            <button key={key} onClick={() => toggle(key)} data-testid={`channel-${key}`} aria-pressed={active}
              className={`flex flex-col items-center justify-center gap-1 min-h-[64px] px-2 py-2 rounded-2xl border-2 font-bold text-sm transition-all ${active ? "bg-brand text-white border-brand" : "bg-white text-ink-soft border-line hover:border-brand/40"}`}>
              <span className="flex items-center gap-2"><Icon size={16} /> {label}</span>
              <span data-testid={`channel-${key}-availability`} className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${active ? "bg-white/20 text-white" : live ? "bg-brand/10 text-brand" : "bg-cream border border-line text-ink-muted"}`}>{availability[key]}</span>
            </button>
          );
        })}
      </div>

      {(wantEmail || wantSms) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {wantEmail && (
            <div data-testid="notify-email-field">
              <label htmlFor="notify-email" className="text-xs font-bold uppercase tracking-wider text-ink-muted">Email for alerts</label>
              <input id="notify-email" data-testid="notify-email-input" type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="you@example.com" className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
              {!emailValid && <p className="text-xs text-brand mt-1">Enter a valid email address.</p>}
            </div>
          )}
          {wantSms && (
            <div data-testid="notify-phone-field">
              <label htmlFor="notify-phone" className="text-xs font-bold uppercase tracking-wider text-ink-muted">Mobile number (for SMS)</label>
              <input id="notify-phone" data-testid="notify-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="(845) 555-1234" className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
              {!smsValid && <p className="text-xs text-brand mt-1">Enter a valid mobile number.</p>}
            </div>
          )}
        </div>
      )}

      <button onClick={save} disabled={!canSave} data-testid="save-notify-prefs" className="w-full h-12 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
        <Check size={16} /> {saving ? "Saving…" : "Save preferences"}
      </button>
      <p className="text-xs text-ink-muted text-center" data-testid="notify-delivery-note">
        <b>In-app:</b> available now. <b>Email digest:</b> {emailLive ? "available — turned on per saved search in Ding Me, never automatically." : "coming soon — when it launches you turn it on per saved search in Ding Me; nobody is enrolled automatically."} <b>SMS:</b> coming soon. Saved-search alerts are sent only after you enable an available channel for that search. Password-reset and contact emails are separate flows and are not affected by these preferences.
      </p>
    </div>
  );
}

function FavoriteBrands({ user, refresh }) {
  const initial = user.favorite_brands || [];
  const initialMode = user.favorite_brand_mode || "prefer";
  const [brands, setBrands] = useState(initial);
  const [mode, setMode] = useState(initialMode);
  const [saving, setSaving] = useState(false);

  const changed = JSON.stringify([...brands].sort()) !== JSON.stringify([...initial].sort()) || mode !== initialMode;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/favorite-brands", { favorite_brands: brands, favorite_brand_mode: mode });
      await refresh();
      toast.success("Preferred brands saved");
    } catch {
      toast.error("Could not save preferred brands");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-line shadow-soft p-6 space-y-4" data-testid="favorite-brands">
      <div>
        <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Star size={18} className="text-brand" /> Preferred brands</h3>
        <p className="text-sm text-ink-muted mt-1">Limited to the brands with official charts on Siblingo today. We'll prefill them when you use Specific Brand in a search — you can always change them just for that search.</p>
      </div>

      <BrandMultiSelect value={brands} onChange={setBrands} testidPrefix="fav-brand" />

      <div role="radiogroup" aria-label="Default brand behavior" className="space-y-2">
        {[{ v: "prefer", label: "Prefer my favorite brands", sub: "Show these first, but include other complete matches if needed." },
          { v: "only", label: "Only show my favorite brands", sub: "Do not show results from other brands." }].map((m) => {
          const active = mode === m.v;
          return (
            <button key={m.v} role="radio" aria-checked={active} onClick={() => setMode(m.v)} data-testid={`fav-mode-${m.v}`}
              className={`w-full text-left p-3 rounded-xl border-2 transition-all ${active ? "border-brand bg-blush" : "border-line bg-white hover:border-brand/40"}`}>
              <span className="flex items-center gap-2 font-bold text-sm text-ink">{active && <Check size={14} className="text-brand" />}{m.label}</span>
              <span className="block text-xs text-ink-soft mt-0.5">{m.sub}</span>
            </button>
          );
        })}
      </div>

      <button onClick={save} disabled={!changed || saving} data-testid="save-favorite-brands"
        className="w-full h-12 rounded-full bg-brand text-white font-bold text-sm hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
        <Star size={16} /> {saving ? "Saving…" : "Save as my preferred brands"}
      </button>
    </div>
  );
}

function Row({ icon: Icon, title, subtitle, onClick, testid, eid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full bg-white rounded-2xl border border-line shadow-soft p-5 flex items-center gap-4 hover:shadow-card transition-all text-left">
      <div className="w-11 h-11 rounded-2xl bg-brand-light flex items-center justify-center"><Icon className="text-brand" size={22} /></div>
      <div className="flex-1">
        {eid ? <Editable as="p" id={eid} className="font-bold text-ink">{title}</Editable> : <p className="font-bold text-ink">{title}</p>}
        <p className="text-sm text-ink-muted">{subtitle}</p>
      </div>
      <ChevronRight className="text-ink-muted" size={20} />
    </button>
  );
}
